import React from "react";
import { IconButton, Paper, Typography } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { makeStyles } from "@material-ui/core/styles";
import { SEASON_DETAIL } from "../../pages/wqScorecard/scorecardCopy";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(1.5, 2),
    maxWidth: 360,
    maxHeight: "60vh",
    overflowY: "auto",
    // A visible edge: the popover opens over a white table and the paper
    // shadow alone does not separate the two.
    border: "1px solid rgba(0, 0, 0, 0.3)",
    borderRadius: 4,
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  title: {
    fontWeight: 600,
    fontSize: "0.9rem",
    marginBottom: 2,
  },
  closeButton: {
    padding: 2,
    marginTop: -2,
    marginRight: -6,
  },
  subtitle: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  table: {
    borderCollapse: "collapse",
    width: "100%",
    fontSize: "0.8rem",
    "& th": {
      textAlign: "left",
      fontWeight: 600,
      padding: "2px 8px 4px 0",
      borderBottom: `1px solid ${theme.palette.divider}`,
      whiteSpace: "nowrap",
    },
    "& td": {
      padding: "2px 8px 2px 0",
      whiteSpace: "nowrap",
    },
    "& td.num, & th.num": {
      textAlign: "right",
    },
  },
  met: {
    color: "#1b5e20",
  },
  exceeded: {
    color: "#b71c1c",
    fontWeight: 600,
  },
  thin: {
    color: theme.palette.text.secondary,
    fontStyle: "italic",
  },
  footnote: {
    marginTop: theme.spacing(1),
    fontSize: "0.72rem",
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
}));

/**
 * Every July to September season behind one reservoir standard cell: its
 * mean, how many samples it rests on, and whether it met the standard. Opens
 * from the calendar button in the cell; the tooltip only summarizes the years.
 */
export default function SeasonDetailPopover({ cell, onClose }) {
  const classes = useStyles();
  if (!cell) return null;

  const units = cell.units && cell.units !== "None" ? ` ${cell.units}` : "";
  const verdictClass = (s) =>
    !s.judged ? classes.thin : s.met ? classes.met : classes.exceeded;

  return (
    <Paper elevation={6} className={classes.root}>
      <div className={classes.header}>
        <Typography className={classes.title}>
          {cell.siteName} — {cell.parameterName}
        </Typography>
        {onClose && (
          <IconButton
            size="small"
            className={classes.closeButton}
            onClick={onClose}
            aria-label="Close season detail"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </div>
      <Typography className={classes.subtitle}>
        {cell.seasonLabel} means against {cell.standardValue}
        {units}. {cell.bandLabel}: {cell.seasonsMet} of {cell.seasonsTotal}{" "}
        seasons met.
      </Typography>
      <table className={classes.table}>
        <thead>
          <tr>
            <th scope="col">{SEASON_DETAIL.columns.year}</th>
            <th scope="col" className="num">
              {SEASON_DETAIL.columns.mean}
              {units}
            </th>
            <th scope="col" className="num">
              {SEASON_DETAIL.columns.n}
            </th>
            <th scope="col">{SEASON_DETAIL.columns.verdict}</th>
          </tr>
        </thead>
        <tbody>
          {cell.seasons.map((s) => (
            <tr key={s.year}>
              <td>{s.year}</td>
              <td className="num">{s.display ?? "n/a"}</td>
              <td className="num">{s.n}</td>
              <td className={verdictClass(s)}>{s.verdict}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Typography className={classes.footnote}>
        {SEASON_DETAIL.footnote}
      </Typography>
    </Paper>
  );
}
